import { soundService } from '../services/soundService';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { BookOpen, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ArrowDown, ArrowUp, Loader2, Check, X as XIcon, Star, Play, Trophy, ArrowRight, Sparkles, User, FileText, Book, Pointer, Crown, Backpack, Clock, Calendar, History, MapPin, Zap, Users, Map, AlertCircle, Globe, Languages, Lock, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { useTheme } from '../contexts/ThemeContext';
import { dbManager } from '../services/db_manager';
import { statsService } from '../services/statsService';
import { exerciseProgressService } from '../services/exerciseProgressService';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { getLocalISOString } from '../utils/timezoneUtils';
import ReactMarkdown from 'react-markdown';
import { translationService } from '../services/translationService';
import { textToSpeechService } from '../services/textToSpeechService';
import { GrammarChoiceScreen, GrammarVideoPlayer } from '../components/GrammarVideoPlayer';
import { NEW_GRAMMAR_LANGUAGES, GRAMMAR_LANG_CODES, GRAMMAR_VIDEO_LANGS } from '../constants/grammarLanguages';
import { translateQuizPrompt, translateQuizExplanation } from '../services/quizTranslationService';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { StudyPageHeader, StudyPageDescription } from '../components/StudyPageHeader';
import { PageTourButton } from '../components/PageTour';
import { AnimatedIcon } from '../components/AnimatedIcon';
import T from '../components/T';
import i18nInstance from '../i18n';

const FLAGS = [
  { id: 'en', label: 'us Inglês', flag: '🇺🇸' },
  { id: 'fr', label: 'FR Francês', flag: '🇫🇷' },
  { id: 'it', label: 'IT Italiano', flag: '🇮🇹' },
  { id: 'es', label: 'ES Espanhol', flag: '🇪🇸' },
  { id: 'de', label: 'DE Alemão', flag: '🇩🇪' },
  { id: 'ja', label: 'JP Japonês', flag: '🇯🇵' },
  { id: 'zh', label: 'CN Mandarim', flag: '🇨🇳' },
  { id: 'pt', label: 'BR Português', flag: '🇧🇷' },
  { id: 'ru', label: 'RU Russo', flag: '🇷🇺' },
  { id: 'hu', label: 'HU Húngaro', flag: '🇭🇺' }
];

const GRAMMAR_TRACKS: Record<string, Record<string, any[]>> = {
  en: {
    a1: [
      { id: 'en_a1_01_verb_to_be', label: 'Verb to Be', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'en_a1_02_subject_pronouns', label: 'Subject Pronouns', icon: <User className="w-8 h-8" /> },
      { id: 'en_a1_03_articles', label: 'Articles (a, an, the)', icon: <FileText className="w-8 h-8" /> },
      { id: 'en_a1_04_plural_nouns', label: 'Plurals (s, es)', icon: <Book className="w-8 h-8" /> },
      { id: 'en_a1_05_demonstrative_pronouns', label: 'Demonstrative Pron.', icon: <Pointer className="w-8 h-8" /> },
      { id: 'en_a1_06_possessive_adjectives', label: 'Possessive Adjectives', icon: <Crown className="w-8 h-8" /> },
      { id: 'en_a1_07_present_simple', label: 'Present Simple', icon: <Clock className="w-8 h-8" /> },
      { id: 'en_a1_08_adverbs_of_frequency', label: 'Adverbs of Frequency', icon: <Calendar className="w-8 h-8" /> },
      { id: 'en_a1_09_prepositions_of_time', label: 'Prepositions (Time)', icon: <History className="w-8 h-8" /> },
      { id: 'en_a1_10_prepositions_of_place', label: 'Prepositions (Place)', icon: <MapPin className="w-8 h-8" /> },
      { id: 'en_a1_11_can_cant', label: 'Can / Can\'t', icon: <Zap className="w-8 h-8" /> },
      { id: 'en_a1_12_object_pronouns', label: 'Object Pronouns', icon: <Users className="w-8 h-8" /> },
      { id: 'en_a1_13_there_is_there_are', label: 'There is / There are', icon: <Map className="w-8 h-8" /> },
      { id: 'en_a1_14_past_simple_verb_to_be', label: 'Past Simple (to be)', icon: <History className="w-8 h-8" /> },
      { id: 'en_a1_15_imperatives', label: 'Imperatives', icon: <AlertCircle className="w-8 h-8" /> },
    ],
    a2: [
      { id: 'en_a2_01_present_continuous', label: 'Present Continuous', icon: <Clock className="w-8 h-8" /> },
      { id: 'en_a2_02_past_simple', label: 'Past Simple', icon: <History className="w-8 h-8" /> },
      { id: 'en_a2_03_comparatives_superlatives', label: 'Comparatives & Superlatives', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'en_a2_04_future_will_going_to', label: 'Future (Will / Going to)', icon: <Zap className="w-8 h-8" /> },
      { id: 'en_a2_05_modals_obligation', label: 'Modals of Obligation', icon: <AlertCircle className="w-8 h-8" /> },
      { id: 'en_a2_06_present_perfect', label: 'Present Perfect', icon: <Star className="w-8 h-8" /> },
      { id: 'en_a2_07_countable_uncountable', label: 'Countable / Uncountable', icon: <Book className="w-8 h-8" /> },
      { id: 'en_a2_08_adverbs_of_manner', label: 'Adverbs of Manner', icon: <FileText className="w-8 h-8" /> },
      { id: 'en_a2_09_prepositions_movement', label: 'Prepositions (Movement)', icon: <MapPin className="w-8 h-8" /> },
      { id: 'en_a2_10_conjunctions', label: 'Conjunctions', icon: <Pointer className="w-8 h-8" /> },
      { id: 'en_a2_11_passive_voice', label: 'Passive Voice', icon: <Users className="w-8 h-8" /> },
      { id: 'en_a2_12_relative_clauses', label: 'Relative Clauses', icon: <BookOpen className="w-8 h-8" /> },
      { id: 'en_a2_13_first_conditional', label: 'First Conditional', icon: <Zap className="w-8 h-8" /> },
      { id: 'en_a2_14_used_to', label: 'Used To', icon: <History className="w-8 h-8" /> },
      { id: 'en_a2_15_reported_speech', label: 'Reported Speech', icon: <Users className="w-8 h-8" /> },
    ],
    b1: [
      { id: 'en_b1_01_past_continuous', label: 'Past Continuous', icon: <Clock className="w-8 h-8" /> },
      { id: 'en_b1_02_present_perfect_continuous', label: 'Present Perfect Continuous', icon: <Star className="w-8 h-8" /> },
      { id: 'en_b1_03_past_perfect', label: 'Past Perfect', icon: <History className="w-8 h-8" /> },
      { id: 'en_b1_04_second_conditional', label: 'Second Conditional', icon: <Zap className="w-8 h-8" /> },
      { id: 'en_b1_05_modal_verbs', label: 'Modal Verbs', icon: <AlertCircle className="w-8 h-8" /> },
      { id: 'en_b1_06_gerund_infinitive', label: 'Gerund & Infinitive', icon: <Book className="w-8 h-8" /> },
      { id: 'en_b1_07_used_to', label: 'Used To', icon: <History className="w-8 h-8" /> },
      { id: 'en_b1_08_tag_questions', label: 'Tag Questions', icon: <FileText className="w-8 h-8" /> },
      { id: 'en_b1_09_relative_clauses', label: 'Relative Clauses', icon: <BookOpen className="w-8 h-8" /> },
      { id: 'en_b1_10_modal_passive', label: 'Modal Passive', icon: <Users className="w-8 h-8" /> },
    ],
    b2: [
      { id: 'en_b2_01_third_conditional', label: 'Third Conditional', icon: <Zap className="w-8 h-8" /> },
      { id: 'en_b2_02_mixed_conditionals', label: 'Mixed Conditionals', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'en_b2_03_passive_reporting_verbs', label: 'Passive Reporting Verbs', icon: <Users className="w-8 h-8" /> },
      { id: 'en_b2_04_causative_form', label: 'Causative Form', icon: <User className="w-8 h-8" /> },
      { id: 'en_b2_05_future_continuous_perfect', label: 'Future Continuous & Perfect', icon: <Clock className="w-8 h-8" /> },
      { id: 'en_b2_06_advanced_articles', label: 'Advanced Articles', icon: <FileText className="w-8 h-8" /> },
      { id: 'en_b2_07_inversion_for_emphasis', label: 'Inversion for Emphasis', icon: <Pointer className="w-8 h-8" /> },
      { id: 'en_b2_08_linking_words', label: 'Linking Words', icon: <Book className="w-8 h-8" /> },
      { id: 'en_b2_09_wish_if_only', label: 'Wish & If Only', icon: <Star className="w-8 h-8" /> },
      { id: 'en_b2_10_advanced_quantifiers', label: 'Advanced Quantifiers', icon: <Crown className="w-8 h-8" /> },
    ],
    c1: [
      { id: 'en_c1_01_subjunctive_mood', label: 'Subjunctive Mood', icon: <Crown className="w-8 h-8" /> },
      { id: 'en_c1_02_participle_clauses', label: 'Participle Clauses', icon: <FileText className="w-8 h-8" /> },
      { id: 'en_c1_03_cleft_sentences', label: 'Cleft Sentences', icon: <Pointer className="w-8 h-8" /> },
      { id: 'en_c1_04_advanced_conditional_inversion', label: 'Advanced Conditional Inversion', icon: <Zap className="w-8 h-8" /> },
      { id: 'en_c1_05_nominalization', label: 'Nominalization', icon: <Book className="w-8 h-8" /> },
      { id: 'en_c1_06_ellipsis_substitution', label: 'Ellipsis & Substitution', icon: <Users className="w-8 h-8" /> },
      { id: 'en_c1_07_advanced_adverbials', label: 'Advanced Adverbials', icon: <Calendar className="w-8 h-8" /> },
      { id: 'en_c1_08_compound_adjectives', label: 'Compound Adjectives', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'en_c1_09_stance_adverbials', label: 'Stance Adverbials', icon: <User className="w-8 h-8" /> },
      { id: 'en_c1_10_emphasis_focus', label: 'Emphasis & Focus', icon: <Star className="w-8 h-8" /> },
    ],
    c2: [
      { id: 'en_c2_01_distant_past_hypotheses', label: 'Distant Past Hypotheses', icon: <History className="w-8 h-8" /> },
      { id: 'en_c2_02_sophisticated_connectors', label: 'Sophisticated Connectors', icon: <BookOpen className="w-8 h-8" /> },
      { id: 'en_c2_03_double_comparatives', label: 'Double Comparatives', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'en_c2_04_complex_modal_passive', label: 'Complex Modal Passive', icon: <Users className="w-8 h-8" /> },
      { id: 'en_c2_05_idiomatic_phrasal_verbs', label: 'Idiomatic Phrasal Verbs', icon: <Pointer className="w-8 h-8" /> },
      { id: 'en_c2_06_stylistic_inversion', label: 'Stylistic Inversion', icon: <Crown className="w-8 h-8" /> },
      { id: 'en_c2_07_advanced_reported_speech', label: 'Advanced Reported Speech', icon: <User className="w-8 h-8" /> },
      { id: 'en_c2_08_rhetorical_figures', label: 'Rhetorical Figures', icon: <Star className="w-8 h-8" /> },
      { id: 'en_c2_09_textual_cohesion', label: 'Textual Cohesion', icon: <FileText className="w-8 h-8" /> },
      { id: 'en_c2_10_register_control', label: 'Register Control', icon: <AlertCircle className="w-8 h-8" /> },
    ]
  },
  fr: {
    a1: [
      { id: 'fr_a1_01_verbe_etre', label: 'Verbe Être', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_a1_02_verbe_avoir', label: 'Verbe Avoir', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_a1_03_pronoms_sujets', label: 'Pronoms Sujets', icon: <User className="w-8 h-8" /> },
      { id: 'fr_a1_04_articles_definis', label: 'Articles Définis', icon: <FileText className="w-8 h-8" /> },
      { id: 'fr_a1_05_articles_indefinis', label: 'Articles Indéfinis', icon: <FileText className="w-8 h-8" /> },
      { id: 'fr_a1_06_genre_et_nombre', label: 'Genre Et Nombre', icon: <Crown className="w-8 h-8" /> },
      { id: 'fr_a1_07_present_des_verbes_er', label: 'Présent Des Verbes-er', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_a1_08_verbes_ir_et_re', label: 'Verbes-ir Et-re', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_a1_09_negation_ne_pas', label: 'Negation Ne Pas', icon: <History className="w-8 h-8" /> },
      { id: 'fr_a1_10_questions_simples', label: 'Questions Simples', icon: <MapPin className="w-8 h-8" /> },
      { id: 'fr_a1_11_adjectifs_qualificatifs', label: 'Adjectifs Qualificatifs', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_a1_12_adjectifs_possessifs', label: 'Adjectifs Possessifs', icon: <Users className="w-8 h-8" /> },
      { id: 'fr_a1_13_prepositions_de_lieu', label: 'Prepositions De Lieu', icon: <Map className="w-8 h-8" /> },
      { id: 'fr_a1_14_heure_et_date', label: 'Heure Et Date', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_a1_15_il_y_a_et_c_est', label: 'Il Y A Et C Est', icon: <BookOpen className="w-8 h-8" /> },
    ],
    a2: [
      { id: 'fr_a2_01_passe_compose', label: 'Passé Composé', icon: <History className="w-8 h-8" /> },
      { id: 'fr_a2_02_imparfait', label: 'Imparfait', icon: <History className="w-8 h-8" /> },
      { id: 'fr_a2_03_futur_proche', label: 'Futur Proche', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_a2_04_futur_simple', label: 'Futur Simple', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_a2_05_pronoms_objets_directs', label: 'Pronoms Objets Directs', icon: <User className="w-8 h-8" /> },
      { id: 'fr_a2_06_pronoms_objets_indirects', label: 'Pronoms Objets Indirects', icon: <User className="w-8 h-8" /> },
      { id: 'fr_a2_07_pronoms_y_et_en', label: 'Pronoms Y Et En', icon: <User className="w-8 h-8" /> },
      { id: 'fr_a2_08_comparatif_et_superlatif', label: 'Comparatif Et Superlatif', icon: <Calendar className="w-8 h-8" /> },
      { id: 'fr_a2_09_adverbes', label: 'Adverbes', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_a2_10_verbes_pronominaux', label: 'Verbes Pronominaux', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_a2_11_expression_de_la_quantite', label: 'Expression De La Quantité', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_a2_12_pronoms_relatifs', label: 'Pronoms Relatifs', icon: <BookOpen className="w-8 h-8" /> },
      { id: 'fr_a2_13_obligation_et_interdiction', label: 'Obligation Et Interdiction', icon: <Map className="w-8 h-8" /> },
      { id: 'fr_a2_14_condition_si', label: 'Condition Si', icon: <AlertCircle className="w-8 h-8" /> },
      { id: 'fr_a2_15_expression_du_temps', label: 'Expression Du Temps', icon: <Clock className="w-8 h-8" /> },
    ],
    b1: [
      { id: 'fr_b1_01_plus_que_parfait', label: 'Plus Que Parfait', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_b1_02_conditionnel_present', label: 'Conditionnel Présent', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_b1_03_discours_indirect', label: 'Discours Indirect', icon: <FileText className="w-8 h-8" /> },
      { id: 'fr_b1_04_subjonctif_introduction', label: 'Subjonctif Introduction', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_b1_05_voix_passive', label: 'Voix Passive', icon: <Pointer className="w-8 h-8" /> },
      { id: 'fr_b1_06_gerondif', label: 'Gérondif', icon: <Crown className="w-8 h-8" /> },
      { id: 'fr_b1_07_connecteurs_logiques', label: 'Connecteurs Logiques', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_b1_08_expression_des_sentiments', label: 'Expression Des Sentiments', icon: <Calendar className="w-8 h-8" /> },
      { id: 'fr_b1_09_hypothese', label: 'Hypothèse', icon: <History className="w-8 h-8" /> },
      { id: 'fr_b1_10_pronoms_relatifs_avances', label: 'Pronoms Relatifs Avances', icon: <BookOpen className="w-8 h-8" /> },
    ],
    b2: [
      { id: 'fr_b2_01_subjonctif_avance', label: 'Subjonctif Avancé', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_b2_02_conditionnel_passe', label: 'Conditionnel Passé', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_b2_03_discours_rapporte', label: 'Discours Rapporté', icon: <FileText className="w-8 h-8" /> },
      { id: 'fr_b2_04_nominalisation', label: 'Nominalisation', icon: <Book className="w-8 h-8" /> },
      { id: 'fr_b2_05_cause', label: 'Cause', icon: <Pointer className="w-8 h-8" /> },
      { id: 'fr_b2_06_consequence', label: 'Consequence', icon: <Crown className="w-8 h-8" /> },
      { id: 'fr_b2_07_but', label: 'But', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_b2_08_opposition', label: 'Opposition', icon: <Calendar className="w-8 h-8" /> },
      { id: 'fr_b2_09_connecteurs_avances', label: 'Connecteurs Avances', icon: <History className="w-8 h-8" /> },
      { id: 'fr_b2_10_registres_de_langue', label: 'Registres De Langue', icon: <MapPin className="w-8 h-8" /> },
    ],
    c1: [
      { id: 'fr_c1_01_subjonctif_complexe', label: 'Subjonctif Complexe', icon: <Zap className="w-8 h-8" /> },
      { id: 'fr_c1_02_style_soutenu', label: 'Style Soutenu', icon: <User className="w-8 h-8" /> },
      { id: 'fr_c1_03_constructions_impersonnelles', label: 'Constructions Impersonnelles', icon: <FileText className="w-8 h-8" /> },
      { id: 'fr_c1_04_discours_formel', label: 'Discours Formel', icon: <Book className="w-8 h-8" /> },
      { id: 'fr_c1_05_modalite', label: 'Modalite', icon: <Pointer className="w-8 h-8" /> },
      { id: 'fr_c1_06_figures_de_style', label: 'Figures De Style', icon: <Crown className="w-8 h-8" /> },
      { id: 'fr_c1_07_connecteurs_academiques', label: 'Connecteurs Academiques', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_c1_08_abstraction', label: 'Abstraction', icon: <Calendar className="w-8 h-8" /> },
      { id: 'fr_c1_09_syntaxe_complexe', label: 'Syntaxe Complexe', icon: <History className="w-8 h-8" /> },
      { id: 'fr_c1_10_langue_des_medias', label: 'Langue Des Médias', icon: <MapPin className="w-8 h-8" /> },
    ],
    c2: [
      { id: 'fr_c2_01_registre_litteraire', label: 'Registre Littéraire', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'fr_c2_02_nuances_semantiques', label: 'Nuances Sémantiques', icon: <User className="w-8 h-8" /> },
      { id: 'fr_c2_03_rhetorique', label: 'Rhétorique', icon: <FileText className="w-8 h-8" /> },
      { id: 'fr_c2_04_variations_stylistiques', label: 'Variations Stylistiques', icon: <Book className="w-8 h-8" /> },
      { id: 'fr_c2_05_expressions_idiomatiques', label: 'Expressions Idiomatiques', icon: <Pointer className="w-8 h-8" /> },
      { id: 'fr_c2_06_argumentation', label: 'Argumentation', icon: <Crown className="w-8 h-8" /> },
      { id: 'fr_c2_07_implicite', label: 'Implicite', icon: <Clock className="w-8 h-8" /> },
      { id: 'fr_c2_08_langue_academique', label: 'Langue Academique', icon: <Calendar className="w-8 h-8" /> },
      { id: 'fr_c2_09_maitrise_du_discours', label: 'Maîtrise Du Discours', icon: <History className="w-8 h-8" /> },
      { id: 'fr_c2_10_registre_et_nuance', label: 'Registre Et Nuance', icon: <MapPin className="w-8 h-8" /> },
    ]
  },
  it: {
    a1: [
      { id: 'it_a1_01_verbo_essere', label: "Che cos'è il verbo 'Essere'?", icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_a1_02_articoli_determinativi_e_indeterminativi', label: 'Articoli Determinativi e Indeterminativi', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_a1_03_sostantivi_regolari', label: 'Sostantivi Regolari', icon: <Book className="w-8 h-8" /> },
      { id: 'it_a1_04_aggettivi_qualificativi', label: 'Aggettivi Qualificativi', icon: <Crown className="w-8 h-8" /> },
      { id: 'it_a1_05_i_verbi_regolari_in_are', label: 'Verbi Regolari in -are', icon: <Clock className="w-8 h-8" /> },
      { id: 'it_a1_06_i_verbi_regolari_in_ere', label: 'Verbi Regolari in -ere', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_a1_07_i_verbi_regolari_in_ire', label: 'Verbi Regolari in -ire', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_a1_08_i_verbi_ausiliari_essere_avere', label: 'Verbi Ausiliari (Essere / Avere)', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_a1_09_articoli_determinativi_indeterminativi', label: 'Articoli (Revisione e Uso)', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_a1_10_i_pronome_personale_soggetto', label: 'Pronomi Personali Soggetto', icon: <User className="w-8 h-8" /> },
      { id: 'it_a1_11_le_preposizioni_semplici', label: 'Preposizioni Semplici', icon: <History className="w-8 h-8" /> },
      { id: 'it_a1_12_le_preposizioni_articolate', label: 'Preposizioni Articolate', icon: <MapPin className="w-8 h-8" /> },
      { id: 'it_a1_13_i_verbi_riflessivi', label: 'Verbi Riflessivi', icon: <Users className="w-8 h-8" /> },
      { id: 'it_a1_14_il_passato_prossimo', label: 'Passato Prossimo', icon: <History className="w-8 h-8" /> },
      { id: 'it_a1_15_i_verbi_modali_potere_volere_dovere', label: 'Verbi Modali (Potere, Volere, Dovere)', icon: <AlertCircle className="w-8 h-8" /> },
    ],
    a2: [
      { id: 'it_a2_01_passe_compose', label: 'L\'imperfetto Indicativo', icon: <History className="w-8 h-8" /> },
      { id: 'it_a2_02_gli_aggettivi_possessivi', label: 'Gli Aggettivi e Pronomi Possessivi', icon: <Users className="w-8 h-8" /> },
      { id: 'it_a2_03_futur_proche', label: 'Il Futuro Semplice', icon: <Clock className="w-8 h-8" /> },
      { id: 'it_a2_04_futur_simple', label: 'Il Futuro Anteriore', icon: <Clock className="w-8 h-8" /> },
      { id: 'it_a2_05_i_pronomi_diretti', label: 'I Pronomi Diretti', icon: <User className="w-8 h-8" /> },
      { id: 'it_a2_06_i_pronomi_indiretti', label: 'I Pronomi Indiretti', icon: <User className="w-8 h-8" /> },
      { id: 'it_a2_07_passato_prossimo_vs_imperfetto', label: 'Passato Prossimo vs Imperfetto', icon: <History className="w-8 h-8" /> },
      { id: 'it_a2_08_i_verbi_riflessivi', label: 'I Pronomi Ci e Ne', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_a2_09_adverbes', label: 'Gli Avverbi di Modo e Tempo', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_a2_10_i_pronomi_combinati', label: 'I Pronomi Combinati', icon: <User className="w-8 h-8" /> },
      { id: 'it_a2_11_i_pronomi_relativi', label: 'I Pronomi Relativi (Che, Cui)', icon: <BookOpen className="w-8 h-8" /> },
      { id: 'it_a2_12_pronoms_relatifs', label: 'I Verbi Pronominali', icon: <BookOpen className="w-8 h-8" /> },
      { id: 'it_a2_13_obligation_et_interdiction', label: 'Espressione dell\'Obbligo e Divieto', icon: <Map className="w-8 h-8" /> },
      { id: 'it_a2_14_condition_si', label: 'La Condizione con Se', icon: <AlertCircle className="w-8 h-8" /> },
      { id: 'it_a2_15_expression_du_temps', label: 'L\'Espressione del Tempo', icon: <Clock className="w-8 h-8" /> },
    ],
    b1: [
      { id: 'it_b1_01_plus_que_parfait', label: 'Il Passato Remoto', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_b1_02_il_trapassato_prossimo', label: 'Il Trapassato Prossimo', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_b1_03_discours_indirect', label: 'Il Condizionale Presente e Passato', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_b1_04_subjonctif_introduction', label: 'Introduzione al Congiuntivo', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_b1_05_il_congiuntivo_presente', label: 'Il Congiuntivo Presente', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_b1_06_il_congiuntivo_passato', label: 'Il Congiuntivo Passato', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_b1_07_il_congiuntivo_imperfetto', label: 'Il Congiuntivo Imperfetto', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_b1_08_expression_des_sentiments', label: 'La Forma Passiva e Gerundio', icon: <Calendar className="w-8 h-8" /> },
      { id: 'it_b1_09_il_periodo_ipotetico', label: 'Il Periodo Ipotetico', icon: <History className="w-8 h-8" /> },
      { id: 'it_b1_10_il_discorso_indiretto', label: 'Il Discorso Indiretto', icon: <FileText className="w-8 h-8" /> },
    ],
    b2: [
      { id: 'it_b2_01_subjonctif_avance', label: 'Il Congiuntivo Trapassato e Avanzato', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_b2_02_il_si_impersonale_e_passivante', label: 'Il Si Impersonale e Passivante', icon: <Pointer className="w-8 h-8" /> },
      { id: 'it_b2_03_discours_rapporte', label: 'Il Discorso Riportato Avanzato', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_b2_04_nominalisation', label: 'La Nominalizzazione', icon: <Book className="w-8 h-8" /> },
      { id: 'it_b2_05_cause', label: 'Esprimere la Causa', icon: <Pointer className="w-8 h-8" /> },
      { id: 'it_b2_06_consequence', label: 'Esprimere la Conseguenza', icon: <Crown className="w-8 h-8" /> },
      { id: 'it_b2_07_but', label: 'Esprimere lo Scopo', icon: <Clock className="w-8 h-8" /> },
      { id: 'it_b2_08_opposition', label: 'Esprimere l\'Opposizione e Concessione', icon: <Calendar className="w-8 h-8" /> },
      { id: 'it_b2_09_connecteurs_avances', label: 'I Connettori Testuali Avanzati', icon: <History className="w-8 h-8" /> },
      { id: 'it_b2_10_registres_de_langue', label: 'I Registri Linguistici', icon: <MapPin className="w-8 h-8" /> },
    ],
    c1: [
      { id: 'it_c1_01_subjonctif_complexe', label: 'Il Congiuntivo nei Periodi Complessi', icon: <Zap className="w-8 h-8" /> },
      { id: 'it_c1_02_style_soutenu', label: 'Lo Stile Sostenuto e Formale', icon: <User className="w-8 h-8" /> },
      { id: 'it_c1_03_constructions_impersonnelles', label: 'Le Costruzioni Impersonali Avanzate', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_c1_04_discours_formel', label: 'Il Discorso Formale ed Epistolare', icon: <Book className="w-8 h-8" /> },
      { id: 'it_c1_05_modalite', label: 'La Modalità ed Espressione del Dubbio', icon: <Pointer className="w-8 h-8" /> },
      { id: 'it_c1_06_figures_de_style', label: 'Le Figure Retoriche e di Stile', icon: <Crown className="w-8 h-8" /> },
      { id: 'it_c1_07_connecteurs_academiques', label: 'I Connettori Accademici', icon: <Clock className="w-8 h-8" /> },
      { id: 'it_c1_08_abstraction', label: 'L\'Astrazione e Linguaggio Specialistico', icon: <Calendar className="w-8 h-8" /> },
      { id: 'it_c1_09_syntaxe_complexe', label: 'La Sintassi Complessa', icon: <History className="w-8 h-8" /> },
      { id: 'it_c1_10_langue_des_medias', label: 'La Lingua dei Media e Giornalismo', icon: <MapPin className="w-8 h-8" /> },
    ],
    c2: [
      { id: 'it_c2_01_registre_litteraire', label: 'Il Registro Letterario e Arcaico', icon: <Sparkles className="w-8 h-8" /> },
      { id: 'it_c2_02_nuances_semantiques', label: 'Le Sfumature Semantiche e Sinonimia', icon: <User className="w-8 h-8" /> },
      { id: 'it_c2_03_rhetorique', label: 'La Retorica e Persuasione Avanzata', icon: <FileText className="w-8 h-8" /> },
      { id: 'it_c2_04_variations_stylistiques', label: 'Le Variazioni Stilistiche d\'Autore', icon: <Book className="w-8 h-8" /> },
      { id: 'it_c2_05_expressions_idiomatiques', label: 'Le Espressioni Idiomatiche Complesse', icon: <Pointer className="w-8 h-8" /> },
      { id: 'it_c2_06_argumentation', label: 'L\'Argomentazione Filosofica e Giuridica', icon: <Crown className="w-8 h-8" /> },
      { id: 'it_c2_07_implicite', label: 'L\'Implicito e l\'Ironia nel Discorso', icon: <Clock className="w-8 h-8" /> },
      { id: 'it_c2_08_langue_academique', label: 'La Lingua Accademica Superiore', icon: <Calendar className="w-8 h-8" /> },
      { id: 'it_c2_09_maitrise_du_discours', label: 'La Padronanza Assoluta del Discorso', icon: <History className="w-8 h-8" /> },
      { id: 'it_c2_10_registre_et_nuance', label: 'Registro, Nuance e Sensibilità Linguistica', icon: <MapPin className="w-8 h-8" /> },
    ]
  }
};

// Trilhas dos idiomas novos (es, pt, de, ru, hu), geradas a partir de constants/grammarLanguages.ts
const NEW_TRACK_ICONS = [Sparkles, BookOpen, User, FileText, Clock, Crown, Star, Zap, MapPin, History, Globe, Languages];
for (const [code, meta] of Object.entries(NEW_GRAMMAR_LANGUAGES)) {
  GRAMMAR_TRACKS[code] = Object.fromEntries(Object.entries(meta.levels).map(([lv, mods]) => [
    lv,
    mods.map((m, i) => {
      const Icon = NEW_TRACK_ICONS[i % NEW_TRACK_ICONS.length];
      return { id: m.id, label: m.label, icon: <Icon className="w-8 h-8" /> };
    }),
  ]));
}

const getGrammarLangPaths = (code: string) => {
  if (code === 'en') return { folder: 'english/language_english', prefix: 'en' };
  if (code === 'it') return { folder: 'italian/language_italian', prefix: 'it' };
  const meta = NEW_GRAMMAR_LANGUAGES[code];
  if (meta) return { folder: meta.folder, prefix: meta.prefix };
  return { folder: 'french/language_french', prefix: 'fr' };
};

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

interface TopicState {
  id: string;
  user_id: string;
  target_language: string;
  topic_id: string;
  is_completed: number;
  platinum?: number;
}

type ViewState = 'roadmap' | 'explanation' | 'quiz' | 'result';

const LOCAL_TRANSLATIONS: Record<string, {
  translatePage: string;
  errorLoading: string;
  backToMenu: string;
  grammar: string;
  learningTrack: string;
  accessNow: string;
  goldenTip: string;
  next: string;
  startQuiz: string;
  correctFeedback: string;
  incorrectFeedback: string;
  continueBtn: string;
  excellentResult: string;
  keepPracticingResult: string;
  correctAnswersCount: string;
  backToTrack: string;
  stillWorking: string;
  doMoreExercises: string;
  scoreWarning: string;
  seeMoreExercises: string;
  back: string;
  platinumSub: string;
}> = {
  pt: {
    translatePage: "Traduzir página",
    errorLoading: "Não foi possível carregar a explicação.",
    backToMenu: "Voltar ao menu gramatical",
    grammar: "Gramática",
    learningTrack: "Trilha de Aprendizado",
    accessNow: "ACESSAR AGORA",
    goldenTip: "Dica de Ouro",
    next: "Próximo",
    startQuiz: "Iniciar Quiz",
    correctFeedback: "Correto!",
    incorrectFeedback: "Incorreto!",
    continueBtn: "Continuar",
    excellentResult: "Excelente! 🎉",
    keepPracticingResult: "Continue Praticando! 💪",
    correctAnswersCount: "Acertos",
    backToTrack: "Voltar para Trilha",
    stillWorking: "Ainda estamos trabalhando nisso! :D",
    doMoreExercises: "Fazer mais exercícios de gramática",
    scoreWarning: "Você precisa tirar nota 7 para concluir este módulo.",
    seeMoreExercises: "Ver mais exercícios",
    back: "Voltar",
    platinumSub: "Você gabaritou as 100 questões e conquistou o troféu de Platina!"
  },
  en: {
    translatePage: "Translate page",
    errorLoading: "Could not load the explanation.",
    backToMenu: "Back to grammar menu",
    grammar: "Grammar",
    learningTrack: "Learning Track",
    accessNow: "ACCESS NOW",
    goldenTip: "Golden Tip",
    next: "Next",
    startQuiz: "Start Quiz",
    correctFeedback: "Correct!",
    incorrectFeedback: "Incorrect!",
    continueBtn: "Continue",
    excellentResult: "Excellent! 🎉",
    keepPracticingResult: "Keep Practicing! 💪",
    correctAnswersCount: "Correct Answers",
    backToTrack: "Back to Track",
    stillWorking: "We are still working on this! :D",
    doMoreExercises: "Do more grammar exercises",
    scoreWarning: "You need to score 7 to complete this module.",
    seeMoreExercises: "See more exercises",
    back: "Back",
    platinumSub: "You got all 100 questions correct and won the Platinum trophy!"
  },
  es: {
    translatePage: "Traducir página",
    errorLoading: "No se pudo cargar la explicación.",
    backToMenu: "Volver al menú de gramática",
    grammar: "Gramática",
    learningTrack: "Ruta de aprendizaje",
    accessNow: "ACCEDER AHORA",
    goldenTip: "Consejo de oro",
    next: "Siguiente",
    startQuiz: "Iniciar cuestionario",
    correctFeedback: "¡Correcto!",
    incorrectFeedback: "¡Incorrecto!",
    continueBtn: "Continuar",
    excellentResult: "¡Excelente! 🎉",
    keepPracticingResult: "¡Sigue practicando! 💪",
    correctAnswersCount: "Aciertos",
    backToTrack: "Volver a la ruta",
    stillWorking: "¡Aún estamos trabajando en esto! :D",
    doMoreExercises: "Hacer más ejercicios de gramática",
    scoreWarning: "Necesitas obtener una puntuación de 7 para completar este módulo.",
    seeMoreExercises: "Ver más ejercicios",
    back: "Volver",
    platinumSub: "¡Acertaste las 100 preguntas y ganaste el trofeo de Platina!"
  },
  fr: {
    translatePage: "Traine la page",
    errorLoading: "Impossible de charger l'explication.",
    backToMenu: "Retour au menu de grammaire",
    grammar: "Grammaire",
    learningTrack: "Parcours d'apprentissage",
    accessNow: "ACCÉDER MAINTENANT",
    goldenTip: "Conseil en or",
    next: "Suivant",
    startQuiz: "Commencer le quiz",
    correctFeedback: "Correct !",
    incorrectFeedback: "Incorrect !",
    continueBtn: "Continuer",
    excellentResult: "Excellent ! 🎉",
    keepPracticingResult: "Continuez à pratiquer ! 💪",
    correctAnswersCount: "Bonnes réponses",
    backToTrack: "Retour au parcours",
    stillWorking: "Nous y travaillons encore ! :D",
    doMoreExercises: "Faire plus d'exercices de grammaire",
    scoreWarning: "Vous devez obtenir une note de 7 pour terminer ce module.",
    seeMoreExercises: "Voir plus d'exercices",
    back: "Retour",
    platinumSub: "Vous avez répondu correctement aux 100 questions et remporté le trophée de Platine !"
  },
  de: {
    translatePage: "Seite übersetzen",
    errorLoading: "Die Erklärung konnte nicht geladen werden.",
    backToMenu: "Zurück zum Grammatikmenü",
    grammar: "Grammatik",
    learningTrack: "Lernpfad",
    accessNow: "JETZT ZUGREIFEN",
    goldenTip: "Goldener Tipp",
    next: "Weiter",
    startQuiz: "Quiz starten",
    correctFeedback: "Richtig!",
    incorrectFeedback: "Falsch!",
    continueBtn: "Weiter",
    excellentResult: "Ausgezeichnet! 🎉",
    keepPracticingResult: "Übe weiter! 💪",
    correctAnswersCount: "Richtige Antworten",
    backToTrack: "Zurück zum Pfad",
    stillWorking: "Wir arbeiten noch daran! :D",
    doMoreExercises: "Mehr Grammatikübungen machen",
    scoreWarning: "Sie müssen eine Punktzahl von 7 erreichen, um dieses Modul abzuschließen.",
    seeMoreExercises: "Mehr Übungen sehen",
    back: "Zurück",
    platinumSub: "Sie haben alle 100 Fragen richtig beantwortet und die Platin-Trophäe gewonnen!"
  },
  it: {
    translatePage: "Traduci pagina",
    errorLoading: "Impossibile caricare la spiegazione.",
    backToMenu: "Torna al menu di grammatica",
    grammar: "Grammatica",
    learningTrack: "Percorso di apprendimento",
    accessNow: "ACCEDI ORA",
    goldenTip: "Consiglio d'oro",
    next: "Avanti",
    startQuiz: "Inizia il quiz",
    correctFeedback: "Corretto!",
    incorrectFeedback: "Errato!",
    continueBtn: "Continua",
    excellentResult: "Eccellente! 🎉",
    keepPracticingResult: "Continua a praticare! 💪",
    correctAnswersCount: "Risposte Corrette",
    backToTrack: "Torna al percorso",
    stillWorking: "Ci stiamo ancora lavorando! :D",
    doMoreExercises: "Fai altri esercizi di grammatica",
    scoreWarning: "Devi ottenere un punteggio di 7 per completare questo modulo.",
    seeMoreExercises: "Vedi altri esercizi",
    back: "Indietro",
    platinumSub: "Hai risposto correttamente a tutte le 100 domande e hai vinto il trofeo di Platino!"
  },
  ja: {
    translatePage: "ページを翻訳",
    errorLoading: "解説を読み込めませんでした。",
    backToMenu: "文法メニューに戻る",
    grammar: "文法",
    learningTrack: "学習トラック",
    accessNow: "今すぐアクセス",
    goldenTip: "ゴールデンヒント",
    next: "次へ",
    startQuiz: "クイズを開始",
    correctFeedback: "正解！",
    incorrectFeedback: "不正解！",
    continueBtn: "続ける",
    excellentResult: "素晴らしい！ 🎉",
    keepPracticingResult: "練習を続けましょう！ 💪",
    correctAnswersCount: "正解数",
    backToTrack: "トラックに戻る",
    stillWorking: "現在作成中です！ :D",
    doMoreExercises: "さらに文法練習を行う",
    scoreWarning: "このモジュールを完了するには、7点以上を獲得する必要があります。",
    seeMoreExercises: "他の演習を見る",
    back: "戻る",
    platinumSub: "100問すべてに正解し、プラチナトロフィーを獲得しました！"
  },
  zh: {
    translatePage: "翻译页面",
    errorLoading: "无法加载解释。",
    backToMenu: "返回语法菜单",
    grammar: "语法",
    learningTrack: "学习路径",
    accessNow: "立即访问",
    goldenTip: "金点子",
    next: "下一步",
    startQuiz: "开始测验",
    correctFeedback: "正确！",
    incorrectFeedback: "错误！",
    continueBtn: "继续",
    excellentResult: "优秀！ 🎉",
    keepPracticingResult: "继续练习！ 💪",
    correctAnswersCount: "正确答案",
    backToTrack: "返回路径",
    stillWorking: "我们还在制作中！ :D",
    doMoreExercises: "进行更多语法练习",
    scoreWarning: "您需要获得 7 分才能完成此模块。",
    seeMoreExercises: "查看更多练习",
    back: "返回",
    platinumSub: "你答对了全部 100 道题，赢得了白金奖杯！"
  },
  ru: {
    translatePage: "Перевести страницу",
    errorLoading: "Не удалось загрузить объяснение.",
    backToMenu: "Вернуться в меню грамматики",
    grammar: "Грамматика",
    learningTrack: "Учебный трек",
    accessNow: "ДОСТУП СЕЙЧАС",
    goldenTip: "Золотой совет",
    next: "Далее",
    startQuiz: "Начать викторину",
    correctFeedback: "Верно!",
    incorrectFeedback: "Неверно!",
    continueBtn: "Продолжить",
    excellentResult: "Отлично! 🎉",
    keepPracticingResult: "Продолжайте практиковаться! 💪",
    correctAnswersCount: "Правильные ответы",
    backToTrack: "Назад к треку",
    stillWorking: "Мы все еще работаем над этим! :D",
    doMoreExercises: "Выполнить больше упражнений",
    scoreWarning: "Вам нужно набрать 7 баллов, чтобы завершить этот модуль.",
    seeMoreExercises: "Посмотреть больше упражнений",
    back: "Назад",
    platinumSub: "Вы правильно ответили на все 100 вопросов и получили Платиновый кубок!"
  },
  hu: {
    translatePage: "Oldal fordítása",
    errorLoading: "Nem sikerült betölteni a magyarázatot.",
    backToMenu: "Vissza a nyelvtani menühöz",
    grammar: "Nyelvtan",
    learningTrack: "Tanulási útvonal",
    accessNow: "BELÉPÉS MOST",
    goldenTip: "Arany tipp",
    next: "Következő",
    startQuiz: "Kvíz indítása",
    correctFeedback: "Helyes!",
    incorrectFeedback: "Helytelen!",
    continueBtn: "Folytatás",
    excellentResult: "Kiváló! 🎉",
    keepPracticingResult: "Gyakorolj tovább! 💪",
    correctAnswersCount: "Helyes válaszok",
    backToTrack: "Vissza az útvonalhoz",
    stillWorking: "Még dolgozunk ezen! :D",
    doMoreExercises: "További nyelvtani feladatok",
    scoreWarning: "A modul elvégzéséhez legalább 7 pontot kell elérned.",
    seeMoreExercises: "További feladatok megtekintése",
    back: "Vissza",
    platinumSub: "Mind a 100 kérdésre helyesen válaszoltál, és megszerezted a Platina trófeát!"
  }
};

const TRANSLATING_LABELS: Record<string, string> = {
  pt: 'Traduzindo...',
  en: 'Translating...',
  es: 'Traduciendo...',
  fr: 'Traduction...',
  de: 'Wird übersetzt...',
  it: 'Traducendo...',
  ru: 'Перевод...',
  zh: '正在翻译...',
  ja: '翻訳中...',
  nl: 'Vertalen...'
};

export default function GrammarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { langCode, level, topicId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isGuide = location.pathname.includes('/guide') || searchParams.get('from') === 'guide';
  const { user } = useAuth();
  const { profile, refreshProfile } = useData();
  const { accentColor } = useTheme();

  const [selectedWordInfo, setSelectedWordInfo] = useState<{
    word: string;
    translation: string;
    loading: boolean;
    x: number;
    y: number;
  } | null>(null);

  const translatingLabel = useMemo(() => {
    return TRANSLATING_LABELS[(i18n.language || 'pt').split('-')[0].toLowerCase()] || TRANSLATING_LABELS.pt;
  }, [i18n.language]);

  useEffect(() => {
    const handleGlobalClick = () => {
      setSelectedWordInfo(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  // Active study tracker initialization & unmount flush for /grammar
  useEffect(() => {
    const rawLang = langCode || profile?.learning_language;
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, langCode, profile?.learning_language]);

  const unifiedDict = useUnifiedDictionary();

  const handleWordClick = async (e: React.MouseEvent<HTMLSpanElement>, word: string) => {
    e.stopPropagation();
    const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    const cleanLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
    const sourceL = (langCode || 'en').split('-')[0].toLowerCase();

    unifiedDict.handleWordClick(e, cleanWord, sourceL, cleanLang);
  };

  const renderClickableText = (text: string, customClassName?: string) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span 
            key={idx}
            onClick={(e) => handleWordClick(e, part)}
            className={`hover:text-primary hover:underline cursor-pointer transition-colors relative inline-block ${customClassName || ''}`}
          >
            {part}
          </span>
        );
      }
      return <span key={idx} className={customClassName || ''}>{part}</span>;
    });
  };

  const currentLangCode = langCode || 'en';
  const selectedLevel = level?.toLowerCase() || 'a1';
  const targetLanguage = currentLangCode === 'en' ? 'english' : currentLangCode === 'it' ? 'italian' : currentLangCode;

  const activeTrack = GRAMMAR_TRACKS[currentLangCode]?.[selectedLevel] || [];

  const [topics, setTopics] = useState<TopicState[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewState>('roadmap');
  const [activeTooltipNodeId, setActiveTooltipNodeId] = useState<string | null>(null);
  
  const [activeNode, setActiveNode] = useState<any>(null);
  const [explanationSteps, setExplanationSteps] = useState<any[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loadingExplanation, setLoadingExplanation] = useState(false);

  // Local Translation & Error States
  const [isPageTranslated, setIsPageTranslated] = useState(false);
  const [translatedSteps, setTranslatedSteps] = useState<Record<number, any>>({});
  const [isTranslatingPage, setIsTranslatingPage] = useState(false);
  const [explanationFetchError, setExplanationFetchError] = useState(false);

  // Quiz Translation States
  const [isQuizTranslated, setIsQuizTranslated] = useState(false);
  const [translatedQuizPrompts, setTranslatedQuizPrompts] = useState<Record<number, string>>({});
  const [translatedQuizExplanations, setTranslatedQuizExplanations] = useState<Record<number, string>>({});
  const [isTranslatingQuiz, setIsTranslatingQuiz] = useState(false);

  const localT = useMemo(() => {
    const lang = i18n.language || 'pt';
    const code = lang.substring(0, 2).toLowerCase();
    return LOCAL_TRANSLATIONS[code] || LOCAL_TRANSLATIONS.pt;
  }, [i18n.language]);

  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalWrong, setTotalWrong] = useState(0);
  const [wrongAnswersList, setWrongAnswersList] = useState<any[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [quizSessionId, setQuizSessionId] = useState('');
  const [isExtendedQuiz, setIsExtendedQuiz] = useState(false);
  const [studyModeChoice, setStudyModeChoice] = useState<'select' | 'text' | 'video'>('select');
  const [showPlatinumCelebration, setShowPlatinumCelebration] = useState(false);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodePreviews, setNodePreviews] = useState<Record<string, { title?: string, explanation?: string }>>({});
  const [loadingPreviewNodeId, setLoadingPreviewNodeId] = useState<string | null>(null);

  const fetchNodePreview = async (nodeId: string) => {
    if (nodePreviews[nodeId]) return;
    setLoadingPreviewNodeId(nodeId);
    try {
      const { folder: langFolder, prefix: langPrefix } = getGrammarLangPaths(currentLangCode);
      const cleanId = nodeId.replace(/_explanation$/, '');

      let url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${langFolder}/${selectedLevel}/${langPrefix}_${selectedLevel}_explanation/${cleanId}.json`;
      let res = await fetch(url);
      if (!res.ok) {
        const urlFallback = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${langFolder}/${selectedLevel}/${langPrefix}_${selectedLevel}_explanation/${cleanId}_explanation.json`;
        res = await fetch(urlFallback);
      }
      if (res.ok) {
        const data = await res.json();
        const firstStep = Array.isArray(data) ? data[0] : data;
        if (firstStep) {
          setNodePreviews(prev => ({
            ...prev,
            [nodeId]: {
              title: firstStep.title || '',
              explanation: firstStep.explanation || ''
            }
          }));
        }
      }
    } catch (e) {
      console.error("Failed to fetch node preview:", e);
    } finally {
      setLoadingPreviewNodeId(null);
    }
  };

  const [showFlagMenu, setShowFlagMenu] = useState(false);
  const [showLevelMenu, setShowLevelMenu] = useState(false);
  const [showNotReady, setShowNotReady] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.grammar-dropdown-container')) {
        setShowLevelMenu(false);
        setShowFlagMenu(false);
      }
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const handleBackToPrevious = () => {
    if (isGuide) {
      navigate('/');
      return;
    }
    goBackOr(navigate, '/study-hub');
  };

  const handleLevelChange = (lvl: string) => {
    setShowLevelMenu(false);
    navigate(`/grammar/${currentLangCode}/${lvl.toLowerCase()}`);
  };

  const handleLangChange = (langId: string) => {
    setShowFlagMenu(false);
    navigate(`/grammar/${langId.toLowerCase()}/${selectedLevel}`);
  };

   const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const roadmapRef = React.useRef<HTMLDivElement>(null);

  const [activeBookPage, setActiveBookPage] = useState(0);
  useEffect(() => {
    setActiveBookPage(0);
  }, [selectedLevel, currentLangCode]);

  useEffect(() => {
    loadTopics();
  }, [targetLanguage]);

  useEffect(() => {
    if (view !== 'roadmap' || activeTrack.length === 0) return;
    
    let targetNodeId = selectedNodeId;
    if (!targetNodeId) {
      const current = activeTrack.find((n, idx) => isNodeUnlocked(n.id, idx) && !isNodeCompleted(n.id)) || activeTrack[0];
      if (current) {
        targetNodeId = current.id;
        setSelectedNodeId(current.id);
      }
    }

    if (targetNodeId && !nodePreviews[targetNodeId] && loadingPreviewNodeId !== targetNodeId) {
      fetchNodePreview(targetNodeId);
    }
  }, [view, activeTrack, selectedNodeId, nodePreviews, loadingPreviewNodeId, topics]);

  useEffect(() => {
    if (topics.length === 0 && loading) return; // wait for topics to load
    
    if (topicId && view === 'roadmap') {
      const nodeIndex = activeTrack.findIndex(n => n.id === topicId);
      if (nodeIndex >= 0) {
        const node = activeTrack[nodeIndex];
        if (isNodeUnlocked(node.id, nodeIndex)) {
          openNodeExplanation(node);
        } else {
          navigate(`/grammar/${currentLangCode}/${selectedLevel}`, { replace: true });
        }
      } else {
        navigate(`/grammar/${currentLangCode}/${selectedLevel}`, { replace: true });
      }
    } else if (!topicId && view !== 'roadmap') {
      setView('roadmap');
    }
  }, [topicId, loading, topics, activeTrack, currentLangCode, selectedLevel]);

  const loadTopics = async () => {
    try {
      const res = await dbManager.execute({
        action: 'get_grammar_topics' as any,
        table: 'user_grammar_topics',
        target_language: targetLanguage
      } as any);
      const validTopics = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setTopics(Array.isArray(validTopics) ? validTopics : []);
    } catch (err) {
      console.error('Failed to load grammar topics:', err);
    } finally {
      setLoading(false);
    }
  };

  const isNodeCompleted = (nodeId: string) => {
    const topic = topics.find(t => t.topic_id === nodeId);
    return topic && topic.is_completed === 1;
  };

  const isNodePlatinum = (nodeId: string) => {
    const topic = topics.find(t => t.topic_id === nodeId);
    return topic && topic.platinum === 1;
  };

  const isNodeUnlocked = (nodeId: string, index: number) => {
    if (index === 0) return true; // First node always unlocked
    const topic = topics.find(t => t.topic_id === nodeId);
    if (topic) return true; // If it's in DB (even not completed), it's unlocked
    // Alternatively, if the previous node is completed, this one is unlocked
    const prevNode = activeTrack[index - 1];
    return isNodeCompleted(prevNode.id);
  };

  const handleNodeClick = async (node: any, index: number) => {
    if (!isNodeUnlocked(node.id, index)) return;
    if (!topicId || topicId !== node.id) {
       navigate(`/grammar/${currentLangCode}/${selectedLevel}/${node.id}`);
    } else {
       openNodeExplanation(node);
    }
  };

  const openNodeExplanation = async (node: any) => {
    setActiveNode(node);
    setView('explanation');
    setStudyModeChoice('select');
    setLoadingExplanation(true);
    setCurrentStepIndex(0);
    setIsPageTranslated(false);
    setTranslatedSteps([]);
    setExplanationFetchError(false);
    setIsExtendedQuiz(false);

    if (user?.id) {
      exerciseProgressService.recordPractice({
        userId: user.id,
        exerciseType: 'grammar_reading',
        langCode: currentLangCode,
        level: selectedLevel,
        itemId: String(node.id),
        score: 100,
        isSuccess: true
      }).catch(() => {});
    }

    try {
      // Fetch explanation steps as JSON
      const { folder: langFolder, prefix: langPrefix } = getGrammarLangPaths(currentLangCode);
      
      let url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${langFolder}/${selectedLevel}/${langPrefix}_${selectedLevel}_explanation/${node.id}.json`;
      let res = await fetch(url);
      if (!res.ok) {
        const urlFallback = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${langFolder}/${selectedLevel}/${langPrefix}_${selectedLevel}_explanation/${node.id}_explanation.json`;
        res = await fetch(urlFallback);
      }

      if (res.ok) {
        const data = await res.json();
        if (data && Array.isArray(data) && data.length > 0) {
           setExplanationSteps(data);
        } else if (data && !Array.isArray(data)) {
           setExplanationSteps([data]);
        } else {
           setExplanationFetchError(true);
        }
      } else {
        setExplanationFetchError(true);
      }
    } catch (err) {
      setExplanationFetchError(true);
    } finally {
      setLoadingExplanation(false);
    }
  };

  const translateTextProtectingQuotes = async (text: string, targetLang: string) => {
    if (!text) return '';
    const placeholders: string[] = [];
    // Match '...' or "..." or “...” or ‘...’
    const protectedText = text.replace(/(['"“‘])(.*?)(['"”’])/g, (match) => {
      placeholders.push(match);
      return `__GRAMMAR_QUOTE_TOKEN_${placeholders.length - 1}__`;
    });

    const res = await translationService.unifiedTranslate(
      protectedText,
      targetLang,
      undefined,
      undefined,
      undefined,
      false,
      false,
      { isGrammar: true }
    );

    let finalTranslation = res.translation || protectedText;
    placeholders.forEach((orig, idx) => {
      const tokenRegex = new RegExp(`__GRAMMAR_QUOTE_TOKEN_${idx}__`, 'gi');
      finalTranslation = finalTranslation.replace(tokenRegex, orig);
    });

    return finalTranslation;
  };

  const translateSingleGrammarStep = async (stepIndex: number) => {
    if (translatedSteps[stepIndex] || !explanationSteps[stepIndex]) return;

    setIsTranslatingPage(true);
    try {
      const targetLangCode = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'pt';
      const step = explanationSteps[stepIndex];
      const titleText = step.title || '';
      const explanationText = step.explanation || '';
      const tipText = step.tip || '';

      const [transTitle, transExplanation, transTip] = await Promise.all([
        titleText ? translateTextProtectingQuotes(titleText, targetLangCode) : Promise.resolve(''),
        explanationText ? translateTextProtectingQuotes(explanationText, targetLangCode) : Promise.resolve(''),
        tipText ? translateTextProtectingQuotes(tipText, targetLangCode) : Promise.resolve('')
      ]);

      const translatedStep = {
        ...step,
        title: transTitle || titleText,
        explanation: transExplanation || explanationText,
        tip: transTip || tipText
      };

      setTranslatedSteps(prev => ({
        ...prev,
        [stepIndex]: translatedStep
      }));
    } catch (err) {
      console.error("Failed to translate grammar step:", err);
    } finally {
      setIsTranslatingPage(false);
    }
  };

  const handleTranslatePage = async () => {
    if (isTranslatingPage) return;
    if (isPageTranslated) {
      setIsPageTranslated(false);
      return;
    }
    setIsPageTranslated(true);
    if (!translatedSteps[currentStepIndex]) {
      await translateSingleGrammarStep(currentStepIndex);
    }
  };

  useEffect(() => {
    if (isPageTranslated && explanationSteps.length > 0 && !translatedSteps[currentStepIndex]) {
      translateSingleGrammarStep(currentStepIndex);
    }
  }, [currentStepIndex, isPageTranslated, explanationSteps]);

  // Quiz Translation
  const translateCurrentQuizQuestion = async (qIdx: number) => {
    const currentQ = quizQuestions[qIdx];
    if (!currentQ) return;

    const targetLangCode = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'pt';
    const sourceLangCode = (currentLangCode || 'en').split('-')[0].toLowerCase();

    setIsTranslatingQuiz(true);
    try {
      if (!translatedQuizPrompts[qIdx]) {
        const rawPrompt = currentQ.question_text || currentQ.question || currentQ.prompt || currentQ.sentence || currentQ.title || currentQ.text || '';
        const translatedP = await translateQuizPrompt(rawPrompt, targetLangCode, sourceLangCode);
        setTranslatedQuizPrompts(prev => ({ ...prev, [qIdx]: translatedP }));
      }

      if (currentQ.wrong_explanation && !translatedQuizExplanations[qIdx]) {
        const translatedE = await translateQuizExplanation(currentQ.wrong_explanation, targetLangCode, sourceLangCode);
        setTranslatedQuizExplanations(prev => ({ ...prev, [qIdx]: translatedE }));
      }
    } catch (err) {
      console.error("[QuizTranslation] Error in GrammarPage:", err);
    } finally {
      setIsTranslatingQuiz(false);
    }
  };

  const handleToggleQuizTranslation = async () => {
    if (isTranslatingQuiz) return;
    if (isQuizTranslated) {
      setIsQuizTranslated(false);
      return;
    }
    setIsQuizTranslated(true);
    await translateCurrentQuizQuestion(currentQuestionIdx);
  };

  useEffect(() => {
    if (isQuizTranslated && quizQuestions.length > 0 && currentQuestionIdx < quizQuestions.length) {
      translateCurrentQuizQuestion(currentQuestionIdx);
    }
  }, [currentQuestionIdx, isQuizTranslated, quizQuestions]);

  const randomizeQuizOptions = (questions: any[]) => {
    if (!Array.isArray(questions)) return [];
    return questions.map((q: any) => {
      if (q && Array.isArray(q.options) && q.options.length > 0) {
        return {
          ...q,
          options: [...q.options].sort(() => 0.5 - Math.random())
        };
      }
      return q;
    });
  };

  const startQuiz = async (isExtended = false) => {
    setView('quiz');
    setQuizSessionId(crypto.randomUUID());
    setTotalCorrect(0);
    setTotalWrong(0);
    setCurrentQuestionIdx(0);
    setWrongAnswersList([]);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsExtendedQuiz(isExtended);

    try {
      const { folder: langFolder, prefix: langPrefix } = getGrammarLangPaths(currentLangCode);
      
      let url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${langFolder}/${selectedLevel}/${langPrefix}_${selectedLevel}_quizes/${activeNode.id}_quizes.json`;
      let res = await fetch(url);
      if (!res.ok) {
        const urlFallback = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${langFolder}/${selectedLevel}/${langPrefix}_${selectedLevel}_quizes/${activeNode.id}.json`;
        res = await fetch(urlFallback);
      }

      if (res.ok) {
        const rawData = await res.json();
        let dataArray = [];
        if (Array.isArray(rawData)) dataArray = rawData;
        else if (rawData && Array.isArray(rawData.data)) dataArray = rawData.data;
        else if (rawData && Array.isArray(rawData.quizes)) dataArray = rawData.quizes;
        else if (rawData && Array.isArray(rawData.quizzes)) dataArray = rawData.quizzes;
        else if (rawData && Array.isArray(rawData.results)) dataArray = rawData.results;
        
        if (dataArray.length > 0) {
          if (isExtended) {
            setQuizQuestions(randomizeQuizOptions(dataArray.slice(0, 100)));
          } else {
            const shuffled = [...dataArray].sort(() => 0.5 - Math.random()).slice(0, 10);
            setQuizQuestions(randomizeQuizOptions(shuffled));
          }
        } else {
          throw new Error("Quiz json was empty or not an array");
        }
      } else {
        throw new Error("HTTP " + res.status);
      }
    } catch (err) {
      console.error("Error loading quiz:", err);
      setExplanationFetchError(true);
      setView('explanation');
    }
  };

  const handleAnswer = (option: any) => {
    if (showFeedback) return;
    
    setSelectedAnswer(option.text);
    const currentQ = quizQuestions[currentQuestionIdx];
    const correct = option.is_correct;
    setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
    setShowFeedback(true);

    const nowStr = getLocalISOString();
    const newTotalCorrect = correct ? totalCorrect + 1 : totalCorrect;
    const newTotalWrong = correct ? totalWrong : totalWrong + 1;

    if (correct) {
      setTotalCorrect(newTotalCorrect);
    } else {
      setTotalWrong(newTotalWrong);
      setWrongAnswersList(prev => [...prev, {
        id: crypto.randomUUID(),
        quiz_session_id: quizSessionId,
        question_id: currentQ.question_id,
        recorded_at: nowStr
      }]);

      const langCodeForError = targetLanguage === 'english' ? 'en' : targetLanguage.substring(0, 2);
      import('../services/errorReviewService').then(({ errorReviewService }) => {
        errorReviewService.recordError({
          userId: user?.id,
          language: langCodeForError,
          exerciseType: 'quizzes',
          sourceOrigin: 'direct_url',
          itemId: currentQ.question_id || `quiz_${currentQuestionIdx}`,
          itemPayload: currentQ,
          userInput: option.text
        });
      });
    }

    // Update Real-time Daily Logs Stats (batched automatically by statsService)
    const langCode = targetLanguage === 'english' ? 'en' : targetLanguage.substring(0, 2);
    statsService.increment('total_quizzes_attempted' as any, user?.id, 1, { langCode });
    if (correct) {
      statsService.increment('total_correct_answers' as any, user?.id, 1, { langCode });
    } else {
      statsService.increment('total_wrong_answers' as any, user?.id, 1, { langCode });
    }

    activeStudyTracker.logExercisePrecision(correct, user?.id, langCode);
    activeStudyTracker.logConsistencySession(user?.id, langCode);

    // Record grammar quiz question in uniqueWordsService
    import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
      const optionTexts = Array.isArray(currentQ.options) ? currentQ.options.map((o: any) => o.text || '').join(' ') : '';
      const qText = `${currentQ.question_text || currentQ.question || ''} ${optionTexts}`;
      uniqueWordsService.recordExerciseCompletion(
        user?.id,
        langCode,
        'quizzes',
        currentQ.question_id || `quiz_${currentQuestionIdx}`,
        qText
      );
    });

    // Send Quiz Progress Update
    setTimeout(() => {
      dbManager.execute({
        action: 'save_quiz_progress' as any,
        table: 'quiz_progress',
        id: quizSessionId,
        target_language: targetLanguage,
        language_level: selectedLevel,
        total_correct: newTotalCorrect,
        total_wrong: newTotalWrong,
        recorded_at: nowStr
      }).catch(err => console.error("Failed to save progress:", err));
    }, 0);

    // Send Wrong Answer with 1-second interval as requested
    if (!correct) {
      setTimeout(() => {
        dbManager.execute({
          action: 'save_quiz_wrong_answers' as any,
          table: 'quiz_wrong_answers',
          wrong_answers: [{
            id: crypto.randomUUID(),
            quiz_session_id: quizSessionId,
            question_id: currentQ.question_id,
            recorded_at: nowStr
          }]
        }).catch(err => console.error("Failed to save wrong answer:", err));
      }, 1000);
    }
  };

  const nextQuestion = () => {
    setShowFeedback(false);
    setSelectedAnswer(null);
    setSelectedWordInfo(null);
    if (currentQuestionIdx < quizQuestions.length - 1) {
      setCurrentQuestionIdx(prev => prev + 1);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    setSelectedWordInfo(null);
    const isPlatinum = isExtendedQuiz && totalCorrect === 100;

    if (isPlatinum) {
      setShowPlatinumCelebration(true);
    } else {
      setView('result');
      const pass = totalCorrect >= 7;

      try {
        if (pass) {
          const currentIndex = activeTrack.findIndex(n => n.id === activeNode.id);
          const nextNode = activeTrack[currentIndex + 1];
          await dbManager.execute({
            action: 'complete_grammar_topic' as any,
            table: 'user_grammar_topics',
            topic_id: activeNode.id,
            target_language: targetLanguage,
            next_topic_id: nextNode ? nextNode.id : null,
            platinum: 0
          } as any);
          loadTopics(); // Refresh topics

          if (user?.id) {
            exerciseProgressService.recordPractice({
              userId: user.id,
              exerciseType: 'grammar_quiz',
              langCode: currentLangCode,
              level: selectedLevel,
              itemId: String(activeNode.id),
              score: Math.round((totalCorrect / Math.max(1, quizQuestions.length)) * 100),
              isSuccess: true
            }).catch(() => {});
          }
        }
      } catch (err) {
        console.error("Error saving quiz results", err);
      }
    }
  };

  const handleDismissPlatinumCelebration = async () => {
    setShowPlatinumCelebration(false);
    setView('result');
    try {
      const currentIndex = activeTrack.findIndex(n => n.id === activeNode.id);
      const nextNode = activeTrack[currentIndex + 1];
      await dbManager.execute({
        action: 'complete_grammar_topic' as any,
        table: 'user_grammar_topics',
        topic_id: activeNode.id,
        target_language: targetLanguage,
        next_topic_id: nextNode ? nextNode.id : null,
        platinum: 1
      } as any);
      loadTopics(); // Refresh topics

      if (user?.id) {
        exerciseProgressService.recordPractice({
          userId: user.id,
          exerciseType: 'grammar_quiz',
          langCode: currentLangCode,
          level: selectedLevel,
          itemId: String(activeNode.id),
          score: 100,
          isSuccess: true
        }).catch(() => {});
      }
    } catch (err) {
      console.error("Error saving platinum results", err);
    }
  };

  const renderTopicCard = (
    node: any,
    index: number,
    completed: boolean,
    platinum: boolean,
    unlocked: boolean,
    current: boolean
  ) => {
    const isSelected = selectedNodeId === node.id || (!selectedNodeId && (current || (index === 0 && unlocked)));
    const preview = nodePreviews[node.id];
    const isLoadingPreview = loadingPreviewNodeId === node.id;

    // Design minimal v1: linha do tempo com orbes numerados (mesma lógica de seleção/acesso)
    const orbClass = completed
      ? (platinum ? 'bg-tint-butter text-ink' : 'bg-tint-sage text-ink')
      : current
      ? 'bg-ink text-surface'
      : unlocked
      ? 'bg-surface text-ink border border-ink/15'
      : 'bg-surface/60 text-muted border border-ink/[0.06]';

    return (
      <motion.div
        key={node.id}
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: Math.min(index % 10, 9) * 0.04 }}
        className="w-full flex gap-4 sm:gap-5"
      >
        {/* trilho: orbe + linha */}
        <div className="flex flex-col items-center shrink-0 pt-1">
          <div className={`relative w-11 h-11 rounded-full flex items-center justify-center font-mono text-[13px] tabular-nums transition-colors duration-500 ${orbClass}`}>
            {current && <span className="absolute -inset-1.5 rounded-full border border-ink/30 animate-ping [animation-duration:2.4s]" />}
            {completed && !platinum ? <Check size={17} strokeWidth={2} />
              : completed && platinum ? <Trophy size={16} strokeWidth={1.75} />
              : !unlocked ? <Lock size={14} strokeWidth={1.75} />
              : String(index + 1).padStart(2, '0')}
          </div>
          <div className={`w-px flex-1 min-h-[18px] mt-2 ${completed ? 'bg-tintd-sage/60' : 'bg-ink/10'}`} />
        </div>

        <div className="flex-1 min-w-0 pb-4">
        {isSelected ? (
          /* Tópico em destaque (selecionado) */
          <div className={`w-full rounded-[28px] p-6 sm:p-7 space-y-5 ${current ? 'bg-tint-butter' : completed ? 'bg-tint-sage' : 'bg-surface border border-black/[0.06] dark:border-white/[0.06]'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink/60 mb-2">
                  {completed ? 'Tópico concluído' : current ? 'Estudar agora' : 'Gramática'}
                </p>
                <h3 className="font-display text-2xl sm:text-[28px] font-light tracking-[-0.04em] leading-tight text-ink">
                  {node.label}
                </h3>
              </div>
              {current && (
                <div className="w-10 h-10 rounded-full bg-ink/10 text-ink flex items-center justify-center shrink-0">
                  <AnimatedIcon motion="pulse" active><Zap size={17} strokeWidth={1.75} /></AnimatedIcon>
                </div>
              )}
            </div>

            <div className="text-ink/75 text-sm leading-relaxed">
              {isLoadingPreview ? (
                <div className="flex items-center gap-3 text-ink/60 py-1">
                  <Loader2 size={15} className="animate-spin" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.14em]"><T k="ui.grammar.loadingExplanation" /></span>
                </div>
              ) : preview?.explanation ? (
                <div className="line-clamp-4 max-h-32 overflow-y-auto custom-scrollbar prose prose-slate dark:prose-invert prose-sm">
                  <ReactMarkdown>{preview.explanation}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm text-ink/60 italic">
                  Clique no botão para abrir a explicação completa (texto ou vídeo).
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleNodeClick(node, index);
              }}
              className="group w-full sm:w-auto h-12 pl-5 pr-2 rounded-full bg-primary text-slate-900 text-sm font-medium inline-flex items-center justify-between sm:justify-start gap-4 cursor-pointer hover:opacity-90"
            >
              <span><T k="ui.grammar.openTopic" /></span>
              <span className="w-8 h-8 rounded-full bg-surface/15 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
                <ArrowRight size={15} strokeWidth={1.75} />
              </span>
            </button>
          </div>
        ) : (
          /* Tópico recolhido */
          <div
            onClick={() => {
              if (unlocked) {
                setSelectedNodeId(node.id);
                fetchNodePreview(node.id);
              }
            }}
            className={`w-full min-h-[44px] py-2.5 flex items-center justify-between gap-3 transition-all duration-300 ${
              !unlocked ? 'cursor-not-allowed opacity-45' : 'cursor-pointer opacity-80 hover:opacity-100 hover:translate-x-1'
            }`}
          >
            <h3 className={`text-base sm:text-lg tracking-[-0.02em] truncate ${unlocked ? 'text-ink' : 'text-muted'}`}>
              {node.label}
            </h3>
            {completed && (
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ok shrink-0">{platinum ? 'Platina' : 'Feito'}</span>
            )}
          </div>
        )}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950 overflow-hidden font-sans select-none">
      {/* Header */}
      <div className="shrink-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/60 dark:border-slate-800/60 p-4 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <AppBackButton
            onClick={handleBackToPrevious}
            fallbackUrl={isGuide ? '/' : '/study-hub'}
          />

          <div data-tour="page-title" className="flex flex-col items-center text-center px-2 min-w-0">
            <h1 className="font-display text-2xl font-light tracking-[-0.04em] leading-none text-ink">
              {localT.grammar}<span className="text-primary">.</span>
            </h1>
            <StudyPageDescription tourKey="grammar" className="mt-1 text-[11px] text-muted leading-snug hidden sm:block max-w-xs" />
          </div>

          <div className="flex items-center gap-2 relative grammar-dropdown-container">
            <PageTourButton tourKey="grammar" />
            {view === 'roadmap' ? (
              <>
                {/* Level Selector */}
                <div data-tour="grammar-level" className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFlagMenu(false);
                      setShowLevelMenu(!showLevelMenu);
                    }}
                    className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-[13px] font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                  >
                    <span>{selectedLevel.toUpperCase()}</span>
                    <ChevronDown size={14} />
                  </button>
                  <AnimatePresence>
                    {showLevelMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className="absolute right-0 top-full mt-2 bg-surface dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 rounded-2xl p-1 z-50 min-w-[80px]"
                      >
                        {LEVELS.map(lvl => (
                          <button
                            key={lvl}
                            onClick={() => handleLevelChange(lvl)}
                            className={`w-full px-3 py-2 rounded-xl text-xs font-normal text-left transition-all uppercase tracking-[0.14em] font-mono ${selectedLevel.toLowerCase() === lvl.toLowerCase() ? 'bg-ink text-surface' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                          >
                            {lvl.toUpperCase()}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Language Flag Selector */}
                <div data-tour="grammar-lang" className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLevelMenu(false);
                      setShowFlagMenu(!showFlagMenu);
                    }}
                    className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                  >
                    <span>{FLAGS.find(f => f.id === currentLangCode)?.flag || '🇺🇸'}</span>
                  </button>
                  <AnimatePresence>
                    {showFlagMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className="absolute right-0 top-full mt-2 bg-surface dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 rounded-2xl p-1 z-50 min-w-[120px]"
                      >
                        {FLAGS.filter(f => GRAMMAR_LANG_CODES.includes(f.id)).map(f => (
                          <button
                            key={f.id}
                            onClick={() => handleLangChange(f.id)}
                            className={`w-full px-3 py-2 rounded-xl text-xs font-semibold text-left flex items-center gap-2 transition-all ${currentLangCode === f.id ? 'bg-ink text-surface' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                          >
                            <span>{f.flag}</span>
                            <span>{f.id.toUpperCase()}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : view === 'explanation' && studyModeChoice === 'text' ? (
              <div className="flex items-center gap-3">
                <span className="text-xs font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">
                  {currentStepIndex + 1}/{explanationSteps.length}
                </span>
                <button
                  onClick={handleTranslatePage}
                  disabled={isTranslatingPage}
                  className={`flex items-center justify-center p-2 rounded-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 ${isPageTranslated ? 'bg-ink text-surface' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                  title={localT.translatePage || "Traduzir"}
                >
                  {isTranslatingPage ? (
                    <Loader2 size={16} className="animate-spin text-primary" />
                  ) : (
                    <Languages size={16} />
                  )}
                </button>
              </div>
            ) : view === 'quiz' ? (
              <div className="flex items-center gap-3">
                <span className="text-xs font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">
                  {currentQuestionIdx + 1}/{quizQuestions.length}
                </span>
                <button
                  onClick={handleToggleQuizTranslation}
                  disabled={isTranslatingQuiz}
                  className={`flex items-center justify-center p-2 rounded-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 ${isQuizTranslated ? 'bg-ink text-surface' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                  title={localT.translatePage || "Traduzir"}
                >
                  {isTranslatingQuiz ? (
                    <Loader2 size={16} className="animate-spin text-primary" />
                  ) : (
                    <Languages size={16} />
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence mode="wait">
          {/* VIEW: ROADMAP */}
          {view === 'roadmap' && GRAMMAR_LANG_CODES.includes(currentLangCode) && activeTrack.length > 0 && (
            <motion.div data-tour="grammar-roadmap"
              key="roadmap"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="absolute inset-0 flex flex-col items-center justify-between p-4 sm:p-6 md:p-8"
              onClick={() => setActiveTooltipNodeId(null)}
            >
              <div className="flex-1 w-full overflow-y-auto custom-scrollbar px-2 py-2">
                <div className="space-y-4 max-w-2xl w-full mx-auto">
                  {(() => {
                    const topicsPerPage = 7;
                    const startIndex = activeBookPage * topicsPerPage;
                    const pageNodes = activeTrack.slice(startIndex, startIndex + topicsPerPage);
                    return pageNodes.map((node, localIdx) => {
                      const index = startIndex + localIdx;
                      const completed = isNodeCompleted(node.id);
                      const platinum = isNodePlatinum(node.id);
                      const unlocked = isNodeUnlocked(node.id, index);
                      const current = unlocked && !completed;

                      return renderTopicCard(node, index, completed, platinum, unlocked, current);
                    });
                  })()}
                </div>
              </div>

              {/* Grid Page Navigation Controls */}
              <div className="flex items-center gap-6 mt-6 shrink-0 z-30">
                <button
                  disabled={activeBookPage === 0}
                  onClick={() => setActiveBookPage(p => Math.max(0, p - 1))}
                  className="w-12 h-12 bg-surface dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 rounded-full flex items-center justify-center text-slate-600 dark:text-slate-300 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:scale-100 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={24} />
                </button>
                <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  Página {activeBookPage + 1} de {Math.max(1, Math.ceil(activeTrack.length / 7))}
                </span>
                <button
                  disabled={(activeBookPage + 1) * 7 >= activeTrack.length}
                  onClick={() => setActiveBookPage(p => p + 1)}
                  className="w-12 h-12 bg-surface dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 rounded-full flex items-center justify-center text-slate-600 dark:text-slate-300 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:scale-100 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </motion.div>
          )}

          {/* VIEW: EXPLANATION */}
          {view === 'explanation' && (
            <motion.div 
              key="explanation"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="absolute inset-0 bg-[var(--bg-main)] flex flex-col"
            >
              {studyModeChoice === 'select' ? (
                <GrammarChoiceScreen
                  videoLocked={!GRAMMAR_VIDEO_LANGS.includes(currentLangCode)}
                  onSelectMode={(mode) => setStudyModeChoice(mode)}
                  titleText="Escolha como quer estudar gramática:"
                  topicTitle={activeNode?.label}
                />
              ) : studyModeChoice === 'video' ? (
                <GrammarVideoPlayer
                  langCode={currentLangCode}
                  level={selectedLevel}
                  nodeId={activeNode?.id || ''}
                  topicTitle={activeNode?.label}
                  onFinishOrStartQuiz={() => startQuiz(false)}
                />
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar pb-32">
                    {loadingExplanation ? (
                      <div className="flex flex-col items-center justify-center h-full gap-4 text-slate-400">
                        <Loader2 className="w-8 h-8 animate-spin" />
                        <span className="text-xs font-normal uppercase font-mono tracking-[0.14em]"><T k="ui.common.loadingContent" /></span>
                      </div>
                    ) : explanationFetchError || explanationSteps.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full max-w-md mx-auto text-center gap-6 animate-in fade-in zoom-in-95 duration-300">
                        <div className="w-16 h-16 rounded-full bg-tint-rose dark:bg-tint-rose/30 flex items-center justify-center text-bad">
                          <AlertCircle size={32} />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold dark:text-ink leading-relaxed">
                            {localT.errorLoading}
                          </h3>
                        </div>
                        <button
                          onClick={() => {
                            window.location.href = 'https://www.libritui.com/grammar/';
                          }}
                          className="px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:scale-105 active:scale-95 transition-all"
                        >
                          {localT.backToMenu}
                        </button>
                      </div>
                    ) : (
                      <AnimatePresence mode="wait">
                        <motion.div 
                          key={currentStepIndex}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="max-w-2xl mx-auto flex flex-col gap-8"
                        >
                          {/* Progresso dos passos: segmentos finos */}
                          <div className="flex items-center gap-1.5">
                            {explanationSteps.map((_, i) => (
                              <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i === currentStepIndex ? 'w-8 bg-ink' : i < currentStepIndex ? 'w-4 bg-ink/40' : 'w-4 bg-ink/10'}`} />
                            ))}
                          </div>
                          {/* Step Title */}
                          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-[-0.03em] text-ink leading-tight">
                            {renderClickableText(
                              isPageTranslated && translatedSteps[currentStepIndex]
                                ? translatedSteps[currentStepIndex].title
                                : explanationSteps[currentStepIndex]?.title || '',
                              'text-3xl sm:text-4xl font-medium tracking-[-0.03em] text-ink'
                            )}
                          </h2>

                          {/* Step Explanation */}
                          <div className="prose prose-slate dark:prose-invert prose-headings:font-medium prose-headings:tracking-tight prose-p:leading-relaxed prose-strong:font-semibold prose-strong:text-ink max-w-none text-ink/80 text-[16px]">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => (
                                  <p className="font-medium leading-relaxed">
                                    {typeof children === 'string'
                                      ? renderClickableText(children)
                                      : Array.isArray(children)
                                      ? children.map((child, idx) =>
                                          typeof child === 'string' ? <React.Fragment key={idx}>{renderClickableText(child)}</React.Fragment> : child
                                        )
                                      : children}
                                  </p>
                                ),
                                li: ({ children }) => (
                                  <li className="font-medium leading-relaxed">
                                    {typeof children === 'string'
                                      ? renderClickableText(children)
                                      : Array.isArray(children)
                                      ? children.map((child, idx) =>
                                          typeof child === 'string' ? <React.Fragment key={idx}>{renderClickableText(child)}</React.Fragment> : child
                                        )
                                      : children}
                                  </li>
                                )
                              }}
                            >
                              {isPageTranslated && translatedSteps[currentStepIndex]
                                ? translatedSteps[currentStepIndex].explanation
                                : explanationSteps[currentStepIndex]?.explanation || ''}
                            </ReactMarkdown>
                          </div>

                          {/* Tip Card */}
                          {(isPageTranslated && translatedSteps[currentStepIndex] ? translatedSteps[currentStepIndex].tip : explanationSteps[currentStepIndex]?.tip) && (
                            <div className="bg-tint-butter text-ink p-6 rounded-[24px] mt-2 relative overflow-hidden">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="w-9 h-9 rounded-full bg-warn text-surface flex items-center justify-center">
                                  <AnimatedIcon motion="spin"><Star size={16} strokeWidth={1.75} fill="currentColor" /></AnimatedIcon>
                                </span>
                                <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-ink/60">
                                  {isPageTranslated 
                                    ? localT.goldenTip 
                                    : (LOCAL_TRANSLATIONS[currentLangCode?.toLowerCase()]?.goldenTip || LOCAL_TRANSLATIONS.en.goldenTip || "Golden Tip")
                                  }
                                </span>
                              </div>
                              <p className="text-ink/80 leading-relaxed text-[15px]">
                                {renderClickableText(isPageTranslated && translatedSteps[currentStepIndex]
                                  ? translatedSteps[currentStepIndex].tip
                                  : explanationSteps[currentStepIndex]?.tip || '')}
                              </p>
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </div>
                  
                  {/* Start Quiz Action Bar */}
                  {!(explanationFetchError || explanationSteps.length === 0) && (
                    <div className="absolute bottom-0 left-0 right-0 pb-6 px-6 bg-gradient-to-t from-[var(--bg-main)] via-[var(--bg-main)] to-transparent pt-12">
                     <div className="max-w-2xl mx-auto w-full">
                      {currentStepIndex === explanationSteps.length - 1 && (
                        <p className="text-xs text-muted text-center mb-3 flex items-center justify-center gap-1.5">
                          <AlertCircle size={13} strokeWidth={1.75} className="text-primary animate-pulse" /> {localT.scoreWarning}
                        </p>
                      )}
                      <div className="flex items-center justify-center gap-3 w-full">
                        {currentStepIndex > 0 && (
                          <button
                            onClick={() => setCurrentStepIndex(prev => prev - 1)}
                            className="w-14 h-14 shrink-0 bg-surface text-ink rounded-full border border-black/[0.06] dark:border-white/[0.06] hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
                            title={localT.back}
                            aria-label={localT.back}
                          >
                            <AnimatedIcon motion="slide"><ChevronLeft size={20} strokeWidth={1.75} /></AnimatedIcon>
                          </button>
                        )}
                        <button
                          onClick={() => {
                             if (currentStepIndex < explanationSteps.length - 1) {
                               setCurrentStepIndex(prev => prev + 1);
                             } else {
                               startQuiz(false);
                             }
                          }}
                          disabled={loadingExplanation}
                          className="group flex-1 h-14 pl-6 pr-2 bg-primary text-slate-900 rounded-full font-medium text-sm hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-between gap-3 disabled:opacity-50"
                        >
                          {currentStepIndex < explanationSteps.length - 1 ? (
                             <>{localT.next} <span className="w-10 h-10 rounded-full bg-surface/15 flex items-center justify-center"><ArrowRight size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" /></span></>
                          ) : (
                             <>{localT.startQuiz} <span className="w-10 h-10 rounded-full bg-surface/15 flex items-center justify-center"><Play size={15} fill="currentColor" className="ml-0.5" /></span></>
                          )}
                        </button>
                        {currentStepIndex === explanationSteps.length - 1 && (
                          <button
                            onClick={() => startQuiz(true)}
                            disabled={loadingExplanation}
                            className="h-14 px-5 bg-tint-butter text-ink rounded-full font-medium text-[13px] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            title={i18nInstance.t('ui.grammar.platinum')}
                          >
                            <AnimatedIcon motion="swing"><Trophy size={16} strokeWidth={1.75} /></AnimatedIcon>
                            <span>100 Quizzes</span>
                          </button>
                        )}
                      </div>
                     </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* VIEW: QUIZ */}
          {view === 'quiz' && (
            <motion.div 
              key="quiz"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="absolute inset-0 bg-[var(--bg-main)] flex flex-col p-6 pb-24 overflow-y-auto custom-scrollbar"
            >
              {quizQuestions.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="flex-1 max-w-2xl mx-auto w-full flex flex-col justify-center relative grammar-quiz-container">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="flex-1 bg-ink/[0.07] h-1 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-ink rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${((currentQuestionIdx) / quizQuestions.length) * 100}%` }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </div>

                  <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted text-center mb-3">quiz</span>
                  <h2 className="font-display text-2xl md:text-4xl font-medium tracking-[-0.03em] text-ink text-center mb-10 leading-tight">
                    {renderClickableText(
                      isQuizTranslated && translatedQuizPrompts[currentQuestionIdx]
                        ? translatedQuizPrompts[currentQuestionIdx]
                        : (quizQuestions[currentQuestionIdx]?.question_text || quizQuestions[currentQuestionIdx]?.question || ''),
                      'text-2xl md:text-4xl font-medium tracking-[-0.03em] text-ink'
                    )}
                  </h2>

                  <div key={currentQuestionIdx} className="grid gap-3">
                    {(quizQuestions[currentQuestionIdx]?.options || []).map((opt: any, i: number) => {
                      const isSelected = selectedAnswer === opt.text;
                      const isRight = opt.is_correct;
                      
                      let btnClass = "bg-surface border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone";
                      let badge = "bg-ink/[0.06] text-ink/60";
                      
                      if (showFeedback) {
                        if (isRight) {
                          btnClass = "bg-ok/20 border-2 border-ok text-ink";
                          badge = "bg-ok text-surface";
                        } else if (isSelected && !isRight) {
                          btnClass = "bg-bad/20 border-2 border-bad text-ink";
                          badge = "bg-bad text-surface";
                        } else {
                          btnClass = "bg-surface border-black/[0.06] dark:border-white/[0.06] text-ink opacity-50";
                        }
                      } else if (isSelected) {
                        btnClass = "bg-tint-lavender border-transparent text-ink";
                        badge = "bg-ink text-surface";
                      }

                      return (
                        <motion.button
                          key={i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                          onClick={() => handleAnswer(opt)}
                          disabled={showFeedback}
                          className={`group w-full p-3 pr-5 rounded-full border text-left flex items-center gap-4 text-[16px] font-medium transition-all ${btnClass} ${!showFeedback ? 'hover:-translate-y-0.5 active:scale-[0.98]' : ''}`}
                        >
                          <span className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-[12px] font-mono transition-colors ${badge}`}>
                            {showFeedback && isRight ? <Check size={16} strokeWidth={2.25} /> : showFeedback && isSelected ? <XIcon size={16} strokeWidth={2.25} /> : String.fromCharCode(65 + i)}
                          </span>
                          <span className="flex-1">{opt.text}</span>
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Word Translation Bubble */}
                  {selectedWordInfo && (
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-[80] bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-3 py-2 rounded-xl text-xs font-semibold select-none pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
                      style={{ 
                        left: `${selectedWordInfo.x}px`, 
                        top: `${selectedWordInfo.y}px`,
                        transform: 'translate(-50%, -100%)'
                      }}
                    >
                      {selectedWordInfo.loading ? (
                        <div className="flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin text-primary" />
                          <span>{translatingLabel}</span>
                        </div>
                      ) : (
                        <span>{selectedWordInfo.translation}</span>
                      )}
                      <div 
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-900 dark:border-t-white"
                      />
                    </div>
                  )}

                  {/* Feedback Bottom Sheet */}
                  <AnimatePresence>
                    {showFeedback && (
                     <div className="fixed bottom-4 inset-x-4 z-50 flex justify-center pointer-events-none">
                      <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className={`pointer-events-auto w-full max-w-2xl p-5 md:p-6 rounded-[28px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] text-ink border-2 ${isCorrect ? 'bg-surface border-ok shadow-ok/30' : 'bg-surface border-bad shadow-bad/30'}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                          <div>
                            <div className="flex items-center gap-3 font-medium text-lg mb-1.5">
                              {isCorrect ? <CheckCircleIcon /> : <XCircleIcon />}
                              {isCorrect ? localT.correctFeedback : localT.incorrectFeedback}
                            </div>
                            {!isCorrect && (
                              <p className="text-sm text-ink/75 leading-relaxed">
                                {isQuizTranslated && translatedQuizExplanations[currentQuestionIdx]
                                  ? translatedQuizExplanations[currentQuestionIdx]
                                  : quizQuestions[currentQuestionIdx]?.wrong_explanation
                                }
                              </p>
                            )}
                          </div>
                          
                          <button
                            onClick={nextQuestion}
                            className="group w-full md:w-auto shrink-0 pl-6 pr-2 py-2 rounded-full font-medium text-sm bg-primary text-slate-900 hover:scale-[1.02] active:scale-95 transition-transform flex items-center justify-between gap-4"
                          >
                            {localT.continueBtn}
                            <span className="w-9 h-9 rounded-full bg-surface/15 flex items-center justify-center"><ArrowRight size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" /></span>
                          </button>
                        </div>
                      </motion.div>
                     </div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          )}

          {/* VIEW: RESULT */}
          {view === 'result' && (
            <motion.div 
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute inset-0 bg-[var(--bg-main)] flex items-center justify-center p-6 overflow-y-auto"
            >
              <div className="max-w-md w-full text-center">
                {(() => {
                  // Anel de acertos: mesma regra de aprovação (>= 7) define o tom
                  const total = Math.max(1, quizQuestions.length);
                  const pct = Math.min(1, totalCorrect / total);
                  const R = 70, C = 2 * Math.PI * R;
                  const good = totalCorrect >= 7;
                  return (
                    <div className={`w-48 h-48 mx-auto mb-8 relative rounded-full ${good ? 'bg-tint-sage' : 'bg-tint-butter'}`}>
                      <svg viewBox="0 0 160 160" className="absolute inset-0 w-full h-full -rotate-90">
                        <circle cx="80" cy="80" r={R} fill="none" stroke="currentColor" strokeWidth="6" className="text-ink/[0.08]" />
                        <motion.circle
                          cx="80" cy="80" r={R} fill="none" strokeWidth="6" strokeLinecap="round"
                          className={good ? 'stroke-ok' : 'stroke-warn'}
                          strokeDasharray={C}
                          initial={{ strokeDashoffset: C }}
                          animate={{ strokeDashoffset: C * (1 - pct) }}
                          transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-ink">
                        <AnimatedIcon motion="swing" active><Trophy size={20} strokeWidth={1.75} className="opacity-60 mb-1" /></AnimatedIcon>
                        <div className="font-display text-5xl font-medium tracking-[-0.04em] tabular-nums">
                          {totalCorrect}<span className="text-2xl text-ink/35">/{quizQuestions.length}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">{localT.correctAnswersCount}</span>
                <h2 className="font-display text-4xl font-medium text-ink mt-2 mb-10 tracking-[-0.03em]">
                  {totalCorrect >= 7 ? localT.excellentResult : localT.keepPracticingResult}
                </h2>

                <button
                  onClick={() => {
                    navigate('/grammar');
                    setView('roadmap');
                  }}
                  className="group w-full h-14 pl-6 pr-2 bg-primary text-slate-900 rounded-full font-medium text-sm hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-between"
                >
                  {localT.backToTrack}
                  <span className="w-10 h-10 rounded-full bg-surface/15 flex items-center justify-center"><ArrowRight size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" /></span>
                </button>

                {!isExtendedQuiz && (totalCorrect >= 7 || (activeNode && isNodeCompleted(activeNode.id))) && (
                  <button
                    onClick={() => startQuiz(true)}
                    className="w-full h-14 mt-3 bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-full font-medium text-sm hover:bg-tint-stone active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <AnimatedIcon motion="bounce"><Plus size={16} strokeWidth={1.75} /></AnimatedIcon>
                    {localT.seeMoreExercises}
                  </button>
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>

        {/* Platinum Celebration Pop-up */}
        <AnimatePresence>
          {showPlatinumCelebration && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-950/95 flex flex-col items-center justify-center p-6 text-center z-[100]"
            >
              <motion.div
                initial={{ scale: 0.8, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.8, y: 50 }}
                transition={{ type: "spring", damping: 15 }}
                className="max-w-md w-full flex flex-col items-center"
              >
                {/* Trophy Glow & Icon */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-slate-200/20 blur-3xl rounded-full scale-125"></div>
                  <div className="w-32 h-32 bg-gradient-to-br from-slate-100 via-[#E2DCD1] to-slate-400 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(229,228,226,0.3)] border border-white/20 relative z-10">
                    <Trophy size={64} className="text-slate-700 drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]" />
                  </div>
                </div>

                <h2 className="text-4xl md:text-5xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-slate-100 via-[#E2DCD1] to-slate-300 mt-6 tracking-wide drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)]">
                  ¡Félicitations!
                </h2>

                <p className="text-slate-300 font-bold mt-4 leading-relaxed max-w-sm">
                  {localT.platinumSub}
                </p>

                <button
                  onClick={handleDismissPlatinumCelebration}
                  className="mt-10 px-8 py-4 bg-gradient-to-r from-slate-100 via-[#E2DCD1] to-slate-200 hover:from-white hover:to-slate-100 text-ink rounded-2xl font-medium text-sm shadow-slate-200/10 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  {localT.continueBtn}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function CheckCircleIcon() {
  return (
    <div className="w-8 h-8 bg-ok rounded-full flex items-center justify-center text-surface">
      <Check size={20} strokeWidth={4} />
    </div>
  );
}

function XCircleIcon() {
  return (
    <div className="w-8 h-8 bg-bad rounded-full flex items-center justify-center text-surface">
      <XIcon size={20} strokeWidth={4} />
    </div>
  );
}
